/**
 * APO (Mageworx) option helpers:
 * - Does not auto-select any option type
 * - Required single-choice groups (radios/swatch/buttons) cannot be cleared
 *   once a value is chosen (shoppers can still switch to another value)
 * - Clear stale checked/selected state on hidden or disabled values
 * - Enforce multiselect selection limits (`min=N; max=N` helptext)
 * - Mirror APO's "Total" (`.mw-apo-price-custom`) into the theme price
 *   display (`.price__current .js-value`, or `.price__current` itself),
 *   including the product form price and the sticky ATC bar.
 *   The product form Add to cart button does not show a price.
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 75;
  let debounceTimer;

  const PRICE_SYNC_DEBOUNCE_MS = 50;
  const PRICE_RETRY_INTERVAL_MS = 200;
  const MAX_PRICE_RETRIES = 40;
  let priceSyncTimer;
  let priceRetryTimer;
  let priceRetryCount = 0;
  var fallbackPriceByRoot = new WeakMap();
  var lastStickyDisplayTotal = '';
  var apoTotalBeforeVariantSwap = '';
  var apoPriceRevision = 0;
  var apoRevisionAtVariantSwap = 0;

  var PRICE_CURRENT_SELECTOR =
    '.product-purchase__price .price__current, .product-info__price .price__current, .product-price .price__current, .product-atc-price .price__current';

  function queryApoRoots() {
    const roots = new Set();
    document.querySelectorAll('#mw-apo-app-block, .mw-apo-container').forEach(function (el) {
      roots.add(el);
    });
    return roots;
  }

  function valueRowIsSelectable(valueEl) {
    if (!valueEl || valueEl.classList.contains('mw-hidden')) return false;
    if (valueEl.classList.contains('mw-option__value--not_available')) return false;
    if (valueEl.classList.contains('mw-avail-option-disabled')) return false;
    var input = valueEl.querySelector('input.mw-option__control');
    return input && !input.disabled;
  }

  function inputHasValidSelection(input) {
    if (!input || !input.checked || input.disabled) return false;
    return valueRowIsSelectable(input.closest('.mw-option__value'));
  }

  var suppressingMultiselectEvents = false;

  function deselectInput(input, silent) {
    if (!input || !input.checked) return;
    var row = input.closest('.mw-option__value');
    input.checked = false;
    if (row) row.classList.remove('mw-option__value--selected');
    if (silent) return;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function clearInvalidSelections(optionEl) {
    var clearedRadio = false;
    optionEl.querySelectorAll('input.mw-option__control:checked').forEach(function (input) {
      if (inputHasValidSelection(input)) return;
      // Silent clear for stale hidden/disabled checks so we don't fight Mageworx event loops.
      deselectInput(input, true);
      if (input.type === 'radio') clearedRadio = true;
    });
    if (clearedRadio) syncRadioSelectedClasses(optionEl);
  }

  function syncRadioSelectedClasses(optionEl) {
    var selected = optionEl.querySelector(
      'input.mw-option__control[type="radio"]:checked:not([disabled])'
    );
    optionEl.querySelectorAll('.mw-option__value').forEach(function (row) {
      row.classList.toggle('mw-option__value--selected', !!(selected && row.contains(selected)));
    });
  }

  function getSelectionLimit(optionEl) {
    var help = optionEl.querySelector('[data-helptext-type="selection_limit"]');
    if (!help) return null;
    var text = help.textContent || '';
    var minMatch = text.match(/min\s*=\s*(\d+)/i);
    var maxMatch = text.match(/max\s*=\s*(\d+)/i);
    if (!minMatch && !maxMatch) return null;
    return {
      min: minMatch ? parseInt(minMatch[1], 10) : null,
      max: maxMatch ? parseInt(maxMatch[1], 10) : null
    };
  }

  function getVisibleCheckedCheckboxes(optionEl) {
    var checked = [];
    optionEl.querySelectorAll('input.mw-option__control[type="checkbox"]:checked').forEach(function (input) {
      var row = input.closest('.mw-option__value');
      if (!row || row.classList.contains('mw-hidden')) return;
      checked.push(input);
    });
    return checked;
  }

  /**
   * Keep selected styling aligned with real visible checked inputs.
   * Mageworx often leaves --selected on hidden/disabled tiles after dependency changes.
   */
  function syncMultiselectSelectedClasses(optionEl) {
    optionEl.querySelectorAll('.mw-option__value').forEach(function (row) {
      var input = row.querySelector('input.mw-option__control[type="checkbox"]');
      if (!input) return;
      var selected = !!(input.checked && !row.classList.contains('mw-hidden'));
      row.classList.toggle('mw-option__value--selected', selected);
    });
  }

  function enforceMultiselectMax(optionEl, newlyCheckedInput) {
    var limits = getSelectionLimit(optionEl);
    if (!limits || limits.max == null || isNaN(limits.max)) return;

    var checked = getVisibleCheckedCheckboxes(optionEl);
    if (checked.length <= limits.max) {
      syncMultiselectSelectedClasses(optionEl);
      return;
    }

    suppressingMultiselectEvents = true;
    try {
      if (newlyCheckedInput && newlyCheckedInput.checked) {
        deselectInput(newlyCheckedInput, true);
      } else {
        while (checked.length > limits.max) {
          deselectInput(checked.pop(), true);
        }
      }
    } finally {
      suppressingMultiselectEvents = false;
    }

    syncMultiselectSelectedClasses(optionEl);

    // One bubbled change so Mageworx/price sync can react without re-entering per-input.
    optionEl.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function processMultiselectOption(optionEl) {
    clearInvalidSelections(optionEl);
    enforceMultiselectMax(optionEl, null);
    syncMultiselectSelectedClasses(optionEl);
  }

  function isMultiselectOption(optionEl) {
    return !!(
      optionEl.classList.contains('mw-option--type-multichoicetile') ||
      optionEl.querySelector('input.mw-option__control[type="checkbox"]')
    );
  }

  function processRequiredOption(optionEl) {
    if (isMultiselectOption(optionEl)) {
      processMultiselectOption(optionEl);
      return;
    }

    clearInvalidSelections(optionEl);
    syncRadioSelectedClasses(optionEl);
  }

  var REQUIRED_DESELECT_IGNORE =
    'button, a, .mw-apo-popup-info, .mw-apo-expand-button, .mw-option__value-qty, input:not(.mw-option__control), select, textarea';

  var restoringRequiredSelection = false;

  function rowLooksSelected(row, input) {
    if (input && input.checked) return true;
    return !!(row && row.classList.contains('mw-option__value--selected'));
  }

  function getRequiredRadioContext(evt) {
    if (!evt || !evt.target || !evt.target.closest) return null;
    if (evt.target.closest(REQUIRED_DESELECT_IGNORE)) return null;

    var valueRow = evt.target.closest('.mw-option__value');
    if (!valueRow) return null;

    var optionEl = valueRow.closest('.mw-option.mw-option--required');
    if (!optionEl || isMultiselectOption(optionEl)) return null;

    var input = valueRow.querySelector('input.mw-option__control[type="radio"]');
    if (!input || input.disabled) return null;
    return { optionEl: optionEl, input: input, row: valueRow };
  }

  function restoreScrollPosition(x, y) {
    window.scrollTo(x, y);
  }

  /**
   * Re-select through Mageworx's own click path so price/state stay in sync.
   * Setting input.checked alone updates the tile but drops the APO total.
   */
  function notifyMageworxSelect(input) {
    var scrollX = window.scrollX;
    var scrollY = window.scrollY;
    var previouslyFocused = document.activeElement;
    var label = input.closest('label');

    restoringRequiredSelection = true;
    try {
      if (label && typeof label.click === 'function') {
        label.click();
      } else {
        input.checked = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } finally {
      restoringRequiredSelection = false;
    }

    if (
      document.activeElement === input ||
      (label && label.contains(document.activeElement))
    ) {
      if (
        previouslyFocused &&
        previouslyFocused !== document.activeElement &&
        typeof previouslyFocused.focus === 'function'
      ) {
        try {
          previouslyFocused.focus({ preventScroll: true });
        } catch (err) {
          previouslyFocused.focus();
        }
      } else if (typeof input.blur === 'function') {
        input.blur();
      }
    }

    restoreScrollPosition(scrollX, scrollY);
    requestAnimationFrame(function () {
      restoreScrollPosition(scrollX, scrollY);
    });
  }

  function restoreRequiredRadio(input, optionEl) {
    if (restoringRequiredSelection || !input || !optionEl) return;
    if (!valueRowIsSelectable(input.closest('.mw-option__value'))) return;

    var selectedInput = optionEl.querySelector(
      'input.mw-option__control[type="radio"]:checked:not([disabled])'
    );
    if (selectedInput && selectedInput !== input) return;

    var selectedRow = optionEl.querySelector('.mw-option__value--selected:not(.mw-hidden)');
    if (selectedRow && !selectedRow.contains(input) && selectedInput !== input) return;

    // Mageworx hasn't finished clearing yet — a click now would toggle off again.
    if (input.checked) return;

    notifyMageworxSelect(input);
    syncRadioSelectedClasses(optionEl);
    schedulePriceSyncRetries();
  }

  /**
   * Do not intercept clicks (that blocked new selections).
   * After Mageworx toggles a required radio off, click it back on so the
   * APO total is restored too — not just the selected class.
   */
  function bindRequiredRadioDeselectGuard(root) {
    if (root.dataset.mwApoRequiredLockBound) return;
    root.dataset.mwApoRequiredLockBound = '1';

    var pendingRestore = null;

    function snapshotIfChecked(evt) {
      if (restoringRequiredSelection) return;
      var ctx = getRequiredRadioContext(evt);
      pendingRestore = ctx && rowLooksSelected(ctx.row, ctx.input) ? ctx : null;
    }

    function restoreIfCleared() {
      if (restoringRequiredSelection) return;
      var ctx = pendingRestore;
      pendingRestore = null;
      if (!ctx) return;
      requestAnimationFrame(function () {
        restoreRequiredRadio(ctx.input, ctx.optionEl);
        setTimeout(function () {
          restoreRequiredRadio(ctx.input, ctx.optionEl);
        }, 50);
      });
    }

    root.addEventListener('pointerdown', snapshotIfChecked, true);
    root.addEventListener('keydown', function (evt) {
      if (evt.key !== ' ' && evt.key !== 'Enter') return;
      snapshotIfChecked(evt);
    }, true);
    root.addEventListener('click', restoreIfCleared);
    root.addEventListener('change', restoreIfCleared);
  }

  function bindMultiselectLimitGuard(root) {
    if (root.dataset.mwApoMultiLimitBound) return;
    root.dataset.mwApoMultiLimitBound = '1';

    // Bubble phase only — let Mageworx finish the check, then reject over-max picks.
    root.addEventListener('change', function (evt) {
      if (suppressingMultiselectEvents) return;
      var input = evt.target;
      if (!input || !input.classList || !input.classList.contains('mw-option__control')) return;
      if (input.type !== 'checkbox') return;

      var optionEl = input.closest('.mw-option');
      if (!optionEl || !isMultiselectOption(optionEl)) return;
      if (!getSelectionLimit(optionEl)) return;

      if (input.checked) {
        enforceMultiselectMax(optionEl, input);
      } else {
        syncMultiselectSelectedClasses(optionEl);
      }
    });
  }

  function run(container) {
    if (!container || !container.querySelectorAll) return;
    // Required options: clear invalid selections. Multiselect with a selection limit: enforce max.
    container.querySelectorAll('.mw-option.mw-option--required').forEach(processRequiredOption);
    container.querySelectorAll('.mw-option--type-multichoicetile').forEach(function (optionEl) {
      if (optionEl.classList.contains('mw-option--required')) return;
      if (!getSelectionLimit(optionEl)) return;
      processMultiselectOption(optionEl);
    });
  }

  function runAll() {
    queryApoRoots().forEach(function (root) {
      bindRequiredRadioDeselectGuard(root);
      bindMultiselectLimitGuard(root);
      run(root);
    });
    observeApoPriceBlocks();
    observeStickyPrices();
    schedulePriceSyncRetries();
    document.dispatchEvent(new CustomEvent('mw-apo:auto-select'));
  }

  var retryTimers = [];

  function clearRunRetries() {
    retryTimers.forEach(clearTimeout);
    retryTimers = [];
  }

  function scheduleRun() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runAll, DEBOUNCE_MS);
  }

  function scheduleRunWithRetries() {
    scheduleRun();
    clearRunRetries();
    [150, 400, 800].forEach(function (delay) {
      retryTimers.push(setTimeout(runAll, delay));
    });
  }

  function normalizeApoPriceForDisplay(text) {
    if (!text) return '';
    var t = String(text).trim();
    t = t.replace(/\s+[A-Z]{3}\s*$/, '');
    return t.trim();
  }

  function parsePriceAmount(text) {
    if (!text) return NaN;
    var normalized = String(text).trim()
      .replace(/\s+[A-Z]{3}\s*$/, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(/,/g, '');
    var amount = parseFloat(normalized);
    return isNaN(amount) ? NaN : amount;
  }

  function isValidDisplayTotal(text) {
    var amount = parsePriceAmount(text);
    return !isNaN(amount) && amount > 0;
  }

  function getApoTotalFromPriceBlock(block) {
    var strong = block.querySelector('.mw-text--style-strong');
    if (!strong) return '';
    return normalizeApoPriceForDisplay(strong.textContent || '');
  }

  function findProductRootForApoBlock(apoBlock) {
    var productRoot = apoBlock.closest('.js-product');
    if (productRoot) return productRoot;
    var info = apoBlock.closest('.product-info');
    if (info) return info.closest('.js-product');
    return null;
  }

  function productHasApo(root) {
    if (!root) return false;
    return !!(
      root.querySelector('.mw-apo-container, #mw-apo-app-block') ||
      root.querySelector('.mw-product-options')
    );
  }

  function collectProductRootsWithApo() {
    var roots = new Set();
    queryApoRoots().forEach(function (apoRoot) {
      var productRoot = apoRoot.closest('.js-product');
      if (productRoot) roots.add(productRoot);
    });
    document.querySelectorAll('.js-product').forEach(function (root) {
      if (productHasApo(root)) roots.add(root);
    });
    return roots;
  }

  function getPriceUpdateTarget(priceCurrent) {
    if (!priceCurrent) return null;
    var jsValue = priceCurrent.querySelector('.js-value');
    return jsValue || priceCurrent;
  }

  function getPriceValueNodes(root) {
    if (!root) return [];
    return Array.prototype.slice
      .call(root.querySelectorAll(PRICE_CURRENT_SELECTOR))
      .map(getPriceUpdateTarget)
      .filter(Boolean);
  }

  function getStickyPriceValueNodes(sticky) {
    if (!sticky) return [];
    return Array.prototype.slice
      .call(sticky.querySelectorAll('.price__current'))
      .map(getPriceUpdateTarget)
      .filter(Boolean);
  }

  var applyingPriceSync = false;

  function parentPriceEl(node) {
    return node && node.closest && node.closest('[data-parent-price][data-variant-price]');
  }

  function isCatalogVariantPrice(node, displayTotal) {
    var priceEl = parentPriceEl(node);
    if (!priceEl) return false;
    var variantCents = Number(priceEl.getAttribute('data-variant-price'));
    var parentCents = Number(priceEl.getAttribute('data-parent-price'));
    if (!Number.isFinite(variantCents) || !parentCents || variantCents === parentCents) return false;
    var cents = Math.round(parsePriceAmount(displayTotal) * 100);
    return cents === variantCents;
  }

  function setNodePriceText(node, displayTotal) {
    if (!node || isCatalogVariantPrice(node, displayTotal)) return;
    if (node.textContent.trim() === displayTotal) return;
    applyingPriceSync = true;
    node.textContent = displayTotal;
    // Release after observers flush so our writes don't re-trigger sync loops.
    setTimeout(function () {
      applyingPriceSync = false;
    }, 0);
  }

  function captureFallbackPrices() {
    document.querySelectorAll('.js-product').forEach(function (root) {
      if (!productHasApo(root) || fallbackPriceByRoot.has(root)) return;
      getPriceValueNodes(root).some(function (node) {
        var text = node.textContent.trim();
        if (isValidDisplayTotal(text)) {
          fallbackPriceByRoot.set(root, text);
          return true;
        }
        return false;
      });
    });
  }

  function getStickyPanelForRoot(root) {
    if (!root) return null;
    var sectionId = root.getAttribute('data-section-id');
    if (sectionId) {
      var byAttr = document.querySelector(
        'sticky-atc-panel[data-dynamic-variant-content="' + sectionId + '-sticky-atc"]'
      );
      if (byAttr) return byAttr;
    }

    // Sticky panel is rendered outside `.js-product` but inside the product section.
    var section = root.closest('.cc-main-product') || root.closest('.shopify-section');
    if (section) {
      var inSection = section.querySelector('sticky-atc-panel');
      if (inSection) return inSection;
    }
    return document.querySelector('sticky-atc-panel');
  }

  function rememberDisplayTotal(root, total) {
    if (!isValidDisplayTotal(total)) return;
    var text = String(total).trim();
    lastStickyDisplayTotal = text;
    if (root) fallbackPriceByRoot.set(root, text);
  }

  function stickyContainerIsZero(container) {
    var nodes = container.querySelectorAll('.price__current');
    if (!nodes.length) return false;
    return Array.prototype.some.call(nodes, function (node) {
      var target = getPriceUpdateTarget(node);
      return !!(target && !isValidDisplayTotal(target.textContent));
    });
  }

  function restoreStickyFromMemory() {
    if (!isValidDisplayTotal(lastStickyDisplayTotal)) return;
    document.querySelectorAll('sticky-atc-panel .sticky-atc-panel__price').forEach(function (container) {
      if (!stickyContainerIsZero(container)) return;
      getStickyPriceValueNodes(container.closest('sticky-atc-panel')).forEach(function (node) {
        setNodePriceText(node, lastStickyDisplayTotal);
      });
    });
  }

  function getBestDisplayTotalForRoot(root, preferredTotal) {
    if (isValidDisplayTotal(preferredTotal)) return String(preferredTotal).trim();

    var mainNodes = getPriceValueNodes(root);
    for (var i = 0; i < mainNodes.length; i++) {
      var mainText = mainNodes[i].textContent.trim();
      if (isValidDisplayTotal(mainText)) return mainText;
    }

    var fallback = fallbackPriceByRoot.get(root);
    return isValidDisplayTotal(fallback) ? fallback : '';
  }

  function setStickyPriceValues(root, displayTotal) {
    var total = getBestDisplayTotalForRoot(root, displayTotal);
    if (!isValidDisplayTotal(total)) return false;
    rememberDisplayTotal(root, total);

    var stickyNodes = getStickyPriceValueNodes(getStickyPanelForRoot(root));
    if (!stickyNodes.length) return false;

    stickyNodes.forEach(function (node) {
      setNodePriceText(node, total);
    });
    return true;
  }

  function restoreFallbackIfZero(root) {
    if (!root || !productHasApo(root)) return;
    var fallback = fallbackPriceByRoot.get(root);
    if (fallback) {
      getPriceValueNodes(root).forEach(function (node) {
        if (!isValidDisplayTotal(node.textContent)) {
          setNodePriceText(node, fallback);
        }
      });
    }
    // Sticky / ATC buttons often get reset to $0 by variant HTML swaps — always re-mirror.
    setPurchasePriceValues(root);
    setStickyPriceValues(root);
    setAtcPriceValues(root);
  }

  function getAtcPriceScope(root) {
    return (
      (root && (root.closest('.cc-main-product') || root.closest('.shopify-section'))) ||
      root ||
      document
    );
  }

  function setAtcPriceValues(root, displayTotal) {
    var total = getBestDisplayTotalForRoot(root, displayTotal);
    if (!isValidDisplayTotal(total)) return false;

    var updated = false;
    getAtcPriceScope(root).querySelectorAll('.product-atc-price .price__current').forEach(function (priceCurrent) {
      var node = getPriceUpdateTarget(priceCurrent);
      if (!node) return;
      setNodePriceText(node, total);
      updated = true;
    });
    return updated;
  }

  function rememberedDisplayTotal(root) {
    var fallback = root && fallbackPriceByRoot.get(root);
    if (isValidDisplayTotal(fallback)) return fallback;
    return isValidDisplayTotal(lastStickyDisplayTotal) ? lastStickyDisplayTotal : '';
  }

  function purchaseAmountText(total) {
    return String(total).trim()
      .replace(/\s+[A-Z]{3}\s*$/, '')
      .replace(/^[A-Z]{3}\s+/, '')
      .replace(/^[^0-9]+/, '')
      .trim();
  }

  function ensurePurchasePriceShape(priceCurrent) {
    if (priceCurrent.querySelector('.js-value') && priceCurrent.querySelector('.product-purchase__currency')) return;
    var block = priceCurrent.closest('[data-currency]');
    var code = block && block.getAttribute('data-currency');
    var amount = purchaseAmountText(priceCurrent.textContent);
    priceCurrent.textContent = '';
    if (code) {
      var currency = document.createElement('span');
      currency.className = 'product-purchase__currency';
      currency.textContent = code;
      priceCurrent.appendChild(currency);
      priceCurrent.appendChild(document.createTextNode(' '));
    }
    var jsValue = document.createElement('span');
    jsValue.className = 'js-value';
    jsValue.textContent = amount;
    priceCurrent.appendChild(jsValue);
  }

  /**
   * Purchase price shows "CAD 179.95" with the code kept outside the amount,
   * so option totals can replace the number without dropping the currency.
   */
  function setPurchasePriceValues(root, displayTotal) {
    var total = getBestDisplayTotalForRoot(root, displayTotal || rememberedDisplayTotal(root));
    if (!isValidDisplayTotal(total)) return false;
    var amount = purchaseAmountText(total) || String(total).trim();

    var updated = false;
    getAtcPriceScope(root).querySelectorAll('.product-purchase__price .price__current').forEach(function (priceCurrent) {
      ensurePurchasePriceShape(priceCurrent);
      var jsValue = priceCurrent.querySelector('.js-value');
      if (jsValue) setNodePriceText(jsValue, amount);
      updated = true;
    });
    return updated;
  }

  function setPriceCurrentValues(root, displayTotal) {
    if (!root || !isValidDisplayTotal(displayTotal)) return false;
    rememberDisplayTotal(root, displayTotal);
    var updated = false;

    getPriceValueNodes(root).forEach(function (node) {
      setNodePriceText(node, displayTotal);
      updated = true;
    });

    if (setPurchasePriceValues(root, displayTotal)) updated = true;
    if (setAtcPriceValues(root, displayTotal)) updated = true;
    if (setStickyPriceValues(root, displayTotal)) updated = true;
    return updated;
  }

  function getAtcPriceTemplate(root) {
    var existing =
      (root && root.querySelector('.product-info__add-button .product-atc-price')) ||
      (root && root.querySelector('.product-atc-price')) ||
      document.querySelector('.product-info__add-button .product-atc-price') ||
      document.querySelector('.product-atc-price');
    if (existing) return existing;

    return (
      (root && root.querySelector('.product-info__price .price')) ||
      document.querySelector('.product-info__price .price')
    );
  }

  function isProductFormAtcButton(button) {
    return !!(button && button.closest('.product-purchase'));
  }

  function ensureAtcButtonPrices() {
    document.querySelectorAll(
      '.mw-apo-add-to-cart-custom, .mulberry-custom-atc, .product-info__add-button [name="add"]'
    ).forEach(function (button) {
      // The product form shows price beside the button, not inside it.
      if (isProductFormAtcButton(button)) return;
      if (button.querySelector('.product-atc-price-wrap')) return;

      var root = button.closest('.js-product') || document.querySelector('.js-product');
      var template = getAtcPriceTemplate(root);
      if (!template) return;

      var wrap = document.createElement('span');
      wrap.className = 'product-atc-price-wrap';
      wrap.innerHTML =
        '<span class="product-atc-price-sep" aria-hidden="true"> | </span><span class="product-atc-price"></span>';
      var dest = wrap.querySelector('.product-atc-price');
      dest.appendChild(template.cloneNode(true));

      var textEl = button.querySelector('.mw-text, .product-atc-label');
      if (textEl) {
        textEl.insertAdjacentElement('afterend', wrap);
      } else {
        button.appendChild(wrap);
      }

      setAtcPriceValues(root);
    });
  }

  function noteVariantPriceSwap() {
    var totals = [];
    document.querySelectorAll('.mw-apo-price-custom').forEach(function (block) {
      var total = getApoTotalFromPriceBlock(block);
      if (isValidDisplayTotal(total)) totals.push(String(total).trim());
    });
    apoTotalBeforeVariantSwap = totals.join('|');
    apoRevisionAtVariantSwap = apoPriceRevision;
  }

  function apoTotalIsStaleVariantSwap(total) {
    if (!apoTotalBeforeVariantSwap) return false;
    if (apoPriceRevision !== apoRevisionAtVariantSwap) return false;
    var text = String(total).trim();
    return apoTotalBeforeVariantSwap.split('|').indexOf(text) !== -1;
  }

  function syncApoPriceDisplays() {
    ensureAtcButtonPrices();
    var syncedRoots = new Set();
    document.querySelectorAll('.mw-apo-price-custom').forEach(function (apoBlock) {
      var total = getApoTotalFromPriceBlock(apoBlock);
      var productRoot = findProductRootForApoBlock(apoBlock);
      if (!productRoot || !isValidDisplayTotal(total)) return;
      // A variant swap already rendered the new price. Don't paint the previous
      // option total back over it until APO recalculates.
      if (apoTotalIsStaleVariantSwap(total)) {
        setPurchasePriceValues(productRoot, total);
        setStickyPriceValues(productRoot, total);
        setAtcPriceValues(productRoot, total);
        return;
      }
      apoTotalBeforeVariantSwap = '';
      if (setPriceCurrentValues(productRoot, total)) {
        syncedRoots.add(productRoot);
      }
    });

    var allSynced = true;
    collectProductRootsWithApo().forEach(function (root) {
      // Keep sticky mirrored even when APO total isn't ready yet.
      if (!setStickyPriceValues(root)) allSynced = false;
      if (syncedRoots.has(root)) return;
      allSynced = false;
      restoreFallbackIfZero(root);
    });
    return allSynced;
  }

  function attemptPriceSyncWithRetry() {
    if (syncApoPriceDisplays()) {
      priceRetryCount = 0;
      return;
    }
    if (priceRetryCount >= MAX_PRICE_RETRIES) {
      priceRetryCount = 0;
      return;
    }
    priceRetryCount += 1;
    priceRetryTimer = setTimeout(attemptPriceSyncWithRetry, PRICE_RETRY_INTERVAL_MS);
  }

  function schedulePriceSyncRetries() {
    clearTimeout(priceRetryTimer);
    priceRetryCount = 0;
    priceRetryTimer = setTimeout(attemptPriceSyncWithRetry, PRICE_RETRY_INTERVAL_MS);
  }

  function schedulePriceSync() {
    clearTimeout(priceSyncTimer);
    priceSyncTimer = setTimeout(function () {
      if (!syncApoPriceDisplays()) {
        schedulePriceSyncRetries();
      }
    }, PRICE_SYNC_DEBOUNCE_MS);
  }

  function observeApoPriceBlocks() {
    document.querySelectorAll('.mw-apo-price-custom').forEach(function (block) {
      if (block.dataset.mwApoPriceSyncObserved) return;
      block.dataset.mwApoPriceSyncObserved = '1';
      var obs = new MutationObserver(function () {
        apoPriceRevision += 1;
        schedulePriceSync();
      });
      obs.observe(block, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class'],
      });
    });
  }

  function observePriceContainers() {
    document.querySelectorAll('.js-product .product-info__price, .js-product .product-price').forEach(function (container) {
      if (container.dataset.mwApoPriceContainerObserved) return;
      container.dataset.mwApoPriceContainerObserved = '1';
      var obs = new MutationObserver(function () {
        var root = container.closest('.js-product');
        var alignPurchasePrice = function () {
          if (!container.classList.contains('product-purchase__price')) return false;
          var total = rememberedDisplayTotal(root);
          if (!isValidDisplayTotal(total)) return false;
          var drifted = false;
          var expected = purchaseAmountText(total) || String(total).trim();
          container.querySelectorAll('.price__current').forEach(function (priceCurrent) {
            var node = getPriceUpdateTarget(priceCurrent);
            var shown = node ? purchaseAmountText(node.textContent) || node.textContent.trim() : '';
            var missingCurrency = priceCurrent.closest('[data-currency]')
              && !priceCurrent.querySelector('.product-purchase__currency');
            if (shown !== expected || missingCurrency) drifted = true;
          });
          if (!drifted) return false;
          setPurchasePriceValues(root, total);
          return true;
        };

        if (applyingPriceSync) {
          // Regios can rewrite the price in the same turn as our write.
          setTimeout(function () {
            if (alignPurchasePrice()) return;
            restoreFallbackIfZero(root);
          }, 0);
          return;
        }
        if (alignPurchasePrice()) return;
        restoreFallbackIfZero(root);
        schedulePriceSyncRetries();
      });
      obs.observe(container, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    });
  }

  function observeStickyPrices() {
    document.querySelectorAll('sticky-atc-panel .sticky-atc-panel__price, .product-atc-price').forEach(function (container) {
      if (container.dataset.mwApoStickyPriceObserved) return;
      container.dataset.mwApoStickyPriceObserved = '1';
      var obs = new MutationObserver(function () {
        var zeroed = stickyContainerIsZero(container);
        if (applyingPriceSync) {
          // A cart/app update can zero the price in the same turn as our own write.
          if (!zeroed) return;
          setTimeout(function () {
            if (!stickyContainerIsZero(container)) return;
            restoreStickyFromMemory();
            schedulePriceSyncRetries();
          }, 0);
          return;
        }
        if (zeroed) restoreStickyFromMemory();
        schedulePriceSyncRetries();
      });
      obs.observe(container, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    });
  }

  function observeAtcButtons() {
    document.querySelectorAll(
      '.product-info__add-button, .mw-apo-add-to-cart-custom, .mulberry-custom-atc'
    ).forEach(function (el) {
      if (el.dataset.mwApoAtcPriceObserved) return;
      el.dataset.mwApoAtcPriceObserved = '1';
      var obs = new MutationObserver(function () {
        if (applyingPriceSync) return;
        ensureAtcButtonPrices();
        schedulePriceSyncRetries();
      });
      obs.observe(el, { childList: true, subtree: true });
    });
  }

  function bindApoInteractionHandlers(root) {
    if (root.dataset.mwApoAutoSelectBound) return;
    root.dataset.mwApoAutoSelectBound = '1';
    root.addEventListener('change', scheduleRunWithRetries, true);
    root.addEventListener('input', scheduleRunWithRetries, true);
    root.addEventListener('click', scheduleRunWithRetries, true);
  }

  function observeRoots() {
    queryApoRoots().forEach(function (root) {
      bindRequiredRadioDeselectGuard(root);
      bindMultiselectLimitGuard(root);
      bindApoInteractionHandlers(root);
      if (root.dataset.mwApoAutoSelectObserved) return;
      root.dataset.mwApoAutoSelectObserved = '1';
      var obs = new MutationObserver(scheduleRunWithRetries);
      obs.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'disabled', 'checked', 'hidden'],
      });
    });
  }

  function init() {
    captureFallbackPrices();
    ensureAtcButtonPrices();
    collectProductRootsWithApo().forEach(restoreFallbackIfZero);
    runAll();
    observeRoots();
    observePriceContainers();
    observeStickyPrices();
    observeAtcButtons();
  }

  function boot() {
    captureFallbackPrices();
    init();
    setTimeout(init, 400);
    setTimeout(init, 1200);
    setTimeout(init, 3000);
    setTimeout(init, 6000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    scheduleRun();
    observeApoPriceBlocks();
    observeStickyPrices();
    observeAtcButtons();
    ensureAtcButtonPrices();
    schedulePriceSyncRetries();
  });
  function restoreStickyAfterCartChange() {
    [0, 250, 800, 1600, 3000].forEach(function (delay) {
      setTimeout(function () {
        restoreStickyFromMemory();
        schedulePriceSyncRetries();
      }, delay);
    });
  }

  document.addEventListener('on:cart:add', restoreStickyAfterCartChange);
  document.addEventListener('on:line-item:change', restoreStickyAfterCartChange);

  document.addEventListener('on:variant:change', function () {
    scheduleRun();
    observePriceContainers();
    schedulePriceSyncRetries();
  });
  document.addEventListener('on:variant:before-replace-element', function (evt) {
    var el = evt.detail && evt.detail.toReplace;
    if (!el) return;
    if (
      el.matches('sticky-atc-panel, [data-dynamic-variant-content*="-price"], [data-dynamic-variant-content*="-sticky-atc"]') ||
      el.querySelector('.price__current, .sticky-atc-panel__price, .product-purchase__price')
    ) {
      noteVariantPriceSwap();
      schedulePriceSyncRetries();
    }
  });

})();
